import { useMemo } from "react";
import { useSelector } from "react-redux";

import { getBrokenLayerNames, getProjectLayers } from "ducks/project/selectors";
import { useSharedLayers } from "hooks/sharing/useSharedLayers";
import { combineLayers, useProjectLayers } from "hooks/project/useProjectLayers";
import { useProjectSrid } from "ducks/project/hooks";
import { getAppConfigShared } from "ducks/appConfig";
import { getTempLayerSourceName } from "../../../ducks/tempLayer/selectors";
import { getTempSourceLayers } from "../../../ducks/tempSourceLayers/selectors";
import { useWidgetConfiguration } from "../../Dashboard/hooks/useWidgetConfiguration";
import { useIsDashboard } from "../../../hooks/location/useIsDashboard";
import { useDashboardLayers } from "../../../hooks/project/useDashboardLayers";

import { WidgetType } from "../../FeatureCard/types";

export const useLayersToSelect = (): { layerNames: string[]; wmsLayers: string[]; mapSr: number } => {
  const brokenLayers = useSelector(getBrokenLayerNames);
  const shared = useSelector(getAppConfigShared);
  const projectLayers = useProjectLayers();
  const sharedLayers = useSharedLayers();
  const currentProjectLayers = useSelector(getProjectLayers);
  const mapSr = useProjectSrid();
  const tempLayerSourceName = useSelector(getTempLayerSourceName);
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const dashboardWidgetConfig = useWidgetConfiguration(WidgetType.Dashboard);
  const dashboardLayers = useMemo(() => dashboardWidgetConfig?.currentConfig?.layers ?? [], [dashboardWidgetConfig]);
  const dashboardLayersDynamicProps = useDashboardLayers();
  const isDashboard = useIsDashboard();

  const layerNames = useMemo(() => {
    const enrichedDashboardLayers = dashboardLayers
      .filter(item => item?.selectable !== false)
      .map(item => ({
        ...item,
        isVisible: dashboardLayersDynamicProps?.layers?.[item.name]?.isVisible,
      }));

    return shared
      ? combineLayers(sharedLayers, { onlyVisible: true }).map(({ name }) => name)
      : isDashboard
      ? enrichedDashboardLayers.map(item => item.name)
      : combineLayers(projectLayers, { onlyVisible: true }).map(({ name }) => name);
  }, [shared, sharedLayers, isDashboard, dashboardLayers, projectLayers, dashboardLayersDynamicProps]);

  const allLayers = useMemo(() => {
    const layerNamesList = [...tempSourceLayers.map(({ name }) => name), ...layerNames];

    return tempLayerSourceName ? [...layerNamesList, tempLayerSourceName] : layerNamesList;
  }, [layerNames, tempLayerSourceName, tempSourceLayers]);

  return useMemo(
    () => ({
      layerNames: allLayers.filter(
        layer => !brokenLayers.includes(layer) && !!currentProjectLayers[layer]?.geometryAttribute,
      ),
      wmsLayers: allLayers.filter(
        layer =>
          currentProjectLayers[layer]?.type === "ProxyService" && !currentProjectLayers[layer]?.geometryAttribute,
      ),
      mapSr,
    }),
    [allLayers, brokenLayers, currentProjectLayers, mapSr],
  );
};
